<script setup>
import { ref, defineProps, defineEmits } from "vue";
import {useI18n} from "vue-i18n";
const { t } = useI18n();

const props = defineProps({
    entity: { type: Object, required: true },
    modelValue: String,
    fetchData: { type: Function, required: true },
    maxlength: { type: Number, default: 50},
    onKeyUp: { type: Boolean, default: true },
    useTooltip: { type: Boolean, default: true },
});

const emit = defineEmits(["update:modelValue"]);

const searchValue = ref(props.modelValue ?? "");
const errorMessage = ref("");

const maxLengthRule = (v) =>
    (v?.length || 0) < props.maxlength ||
    t('common.messages.info.enterMaximumCharacters', { count: props.maxlength })

function handleTyping(e) {
    const value = e.target.value;
    const result = maxLengthRule(value);

    if (result === true) {
        errorMessage.value = "";
        searchValue.value = value;
    } else {
        errorMessage.value = result;
        searchValue.value = value.slice(0, props.maxlength);
    }
    emit("update:modelValue", searchValue.value);
}

async function handleSearch() {
    emit("update:modelValue", searchValue.value);
    const quickSearchConfig = props.entity?.pages?.list?.filters?.quickSearch;

    if (!quickSearchConfig?.searchBy?.length) return;

    const searchField = quickSearchConfig.searchBy[0];

    const params = {
        [searchField]: searchValue.value ? `%${searchValue.value}%` : "",
        page: 1
    };

    await props.fetchData(params);
}
</script>

<template>
    <form class="search-container" @submit.prevent="handleSearch">
        <i
            v-if="useTooltip"
            class="mx-3 ri-information-line text-black"
            v-tooltip="$t('common.messages.info.searchByName')"
        ></i>

        <div class="input-wrapper">
            <input
                type="text"
                class="input-search search-input text-black"
                :class="{ 'has-icon': !onKeyUp }"
                :placeholder="$t('common.labels.searchHere')"
                :value="searchValue"
                @input="handleTyping"
                :maxlength="props.maxlength"
                :title="useTooltip ? $t('common.messages.info.searchByName') : ''"
            />
            <i
                v-if="!onKeyUp"
                class="material-symbols-outlined input-icon"
            >
                search
            </i>
        </div>
        <button v-if="onKeyUp" type="submit" class="search-btn">
            <i class="material-symbols-outlined">search</i>
        </button>
    </form>

</template>

<style scoped>
.search-container {
    display: flex;
    align-items: center;
    gap: 8px;
}

.search-input {
    flex: 1;
    padding: 8px 12px;
}
.search-btn {
    width: 47px;
    height: 38px;
    border: none;
    border-radius: 6px;
    background: #f6f7f9;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.dark-theme .search-btn {
    background: #15203c;
}
.search-btn i {
    font-size: 20px;
}

.input-wrapper {
    position: relative;
    width: 100%;
}

.input-search.has-icon {
    padding-right: 40px;
}

.input-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #555;
    pointer-events: none;
}

</style>
